import type {DateTime, Duration} from "luxon";
import type {If, IsNullable, IsOptional} from "type-fest";

export type Optional<T> = T | undefined;
export type NonOptional<T> = Exclude<T, undefined>;

export type Nullable<T> = T | null;
export type NonNullable<T> = Exclude<T, null>;

export type OptionalNullable<T> = Optional<T> | Nullable<T>;
export type NonOptionalNullable<T> = Exclude<T, undefined | null>;

export function isNonOptional<T>(value: Optional<T>): value is NonOptional<T> {
  return value !== undefined;
}

export function isNonNullable<T>(value: Nullable<T>): value is NonNullable<T> {
  return value !== null;
}

export function isNonOptionalNullable<T>(value: OptionalNullable<T>): value is NonOptionalNullable<T> {
  return isNonOptional(value) && isNonNullable(value);
}


type PseudoPrimitiveTypes = Date | DateTime | Duration | unknown[] | readonly unknown[];

type SameType<TType, TExpectedType, TResult> = [TType] extends [TExpectedType]
  ? (undefined extends TType ? (undefined extends TExpectedType ? true : false) : true) extends true
    ? (null extends TType ? (null extends TExpectedType ? true : false) : true) extends true
      ? TResult
      : never
    : never
  : never;

type AsNullOrOptional<T, TRelated> = If<IsOptional<TRelated>,
  If<IsNullable<TRelated>, OptionalNullable<T>, Optional<T>>,
  If<IsNullable<TRelated>, Nullable<T>, T>>;

type FieldOfHelper<
  TObject extends object,
  TFieldType,
  TPrefix extends string = "",
  TCurrentNullability extends OptionalNullable<never> = never
> =
  {
    [K in keyof TObject]-?: TObject[K] extends infer TChild
    ? NonOptionalNullable<TChild> extends object
      ? NonOptionalNullable<TChild> extends PseudoPrimitiveTypes
        ? SameType<AsNullOrOptional<TChild, TCurrentNullability>, TFieldType, `${TPrefix}${Exclude<K, symbol>}`>
        : FieldOfHelper<NonOptionalNullable<TChild>, TFieldType, `${TPrefix}${Exclude<K, symbol>}.`, AsNullOrOptional<TCurrentNullability, TChild>>
      : SameType<AsNullOrOptional<TChild, TCurrentNullability>, TFieldType, `${TPrefix}${Exclude<K, symbol>}`>
    : never;
  }[keyof TObject];

// We must use `any` here, or some checks will fail.
// `never` leads to the result being never (instead of string).
// `unknown` leads to the result being unknown (instead of string).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FieldOf<TObject extends object, TValue = any> = FieldOfHelper<TObject, TValue>;

type FieldKey<T, K extends string> = (K extends `${infer R extends number}` ? R : K) & keyof T;
export type TypeOfField<
  TObject extends object,
  TField extends FieldOf<TObject>,
> = TField extends `${infer K}.${infer Rest}`
  ? TObject[FieldKey<TObject, K>] extends infer S
    ? S extends object
      ? Rest extends FieldOf<S>
        ? TypeOfField<S, Rest>
        : never
      : S extends undefined
        ? undefined
        : S extends null
          ? null
          : never
    : never
  : TObject[FieldKey<TObject, TField>];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ValueFunctionOf<TObject extends object, TValue = any> = (obj: TObject) => TValue;

export type TypeOfValueFunction<TObject extends object, TFunction extends ValueFunctionOf<TObject, unknown>> = ReturnType<TFunction>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FieldOrFunctionOf<TObject extends object, TValue = any>
  = FieldOf<TObject, TValue> | ValueFunctionOf<TObject, TValue>;

export type TypeOfFieldOrFunction<TObject extends object, TFieldOrFunction extends FieldOrFunctionOf<TObject>> =
  TFieldOrFunction extends ValueFunctionOf<TObject>
    ? TypeOfValueFunction<TObject, TFieldOrFunction>
    : TFieldOrFunction extends FieldOf<TObject>
      ? TypeOfField<TObject, TFieldOrFunction>
      : never;


// @ts-expect-error: It's not, in fact, infinite...
export function isValueFunctionOf<TObject extends object, TValue>(
  provider: FieldOrFunctionOf<TObject, TValue>,
): provider is ValueFunctionOf<TObject, TValue>;
export function isValueFunctionOf<TObject extends object>(
  provider: FieldOrFunctionOf<TObject>,
): provider is ValueFunctionOf<TObject>;
export function isValueFunctionOf<TObject extends object, TValue>(
  provider: FieldOrFunctionOf<TObject, TValue>,
): provider is ValueFunctionOf<TObject, TValue> {
  return typeof provider === "function";
}

export function isFieldOf<TObject extends object, TValue>(
  provider: FieldOrFunctionOf<TObject, TValue>,
): provider is FieldOf<TObject, TValue>;
export function isFieldOf<TObject extends object>(
  provider: FieldOrFunctionOf<TObject>,
): provider is FieldOf<TObject>;
export function isFieldOf<TObject extends object, TValue>(
  provider: FieldOrFunctionOf<TObject, TValue>,
): provider is FieldOf<TObject, TValue> {
  return !isValueFunctionOf(provider);
}

// export function getValue<TObject extends object, TAccessor extends FieldOrFunctionOf<TObject>, TValue extends TypeOfFieldOrFunction<TObject, TAccessor>>(object: TObject, accessor: TAccessor): TValue;
// export function getValue<TObject extends object, TAccessor extends FieldOrFunctionOf<TObject, TValue>, TValue>(object: TObject, accessor: TAccessor): TValue;
export function getValue<TObject extends object, TValue>(object: TObject, provider: FieldOrFunctionOf<TObject, TValue>): TValue {
  return isValueFunctionOf(provider) ? provider(object) : getValueOfField(object, provider);
}

export function getValueOfField<
  TObject extends object, TField extends FieldOf<TObject, TValue>, TValue
>(obj: TObject, path: TField): TValue;
export function getValueOfField<
  TObject extends object, TField extends FieldOf<TObject>, TValue extends TypeOfField<TObject, TField>
>(obj: TObject, path: TField): TValue;
export function getValueOfField<
  TObject extends object,
  TField extends FieldOf<TObject> | FieldOf<TObject, TValue>,
  TValue
>(obj: TObject, path: TField): TValue {
  const parts = path.split(".");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let result: any = obj;
  for (const part of parts) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
    result = result[part];
    if (result === undefined || result === null) break;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return result;
}

export function setValueOfField<
  TObject extends object, TField extends FieldOf<TObject, TValue>, TValue
>(obj: TObject, path: TField, newValue: NoInfer<TValue>, initializeIntermediateOptionalNullableValues?: true): true;
export function setValueOfField<
  TObject extends object, TField extends FieldOf<TObject, TValue>, TValue
>(obj: TObject, path: TField, newValue: NoInfer<TValue>, initializeIntermediateOptionalNullableValues: boolean): boolean;
export function setValueOfField<
  TObject extends object, TField extends FieldOf<TObject>, TValue extends TypeOfField<TObject, TField>
>(obj: TObject, path: TField, newValue: NoInfer<TValue>, initializeIntermediateOptionalNullableValues?: true): true;
export function setValueOfField<
  TObject extends object, TField extends FieldOf<TObject>, TValue extends TypeOfField<TObject, TField>
>(obj: TObject, path: TField, newValue: NoInfer<TValue>, initializeIntermediateOptionalNullableValues: boolean): boolean;
export function setValueOfField<
  TObject extends object, TField extends FieldOf<TObject> | FieldOf<TObject, TValue>, TValue
>(obj: TObject, path: TField, newValue: TValue, initializeIntermediateOptionalNullableValues: boolean = true): boolean {
  const parts = path.split(".");
  const lastIndex = parts.length - 1;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let currentObject: any = obj;
  for (let i = 0; i < lastIndex; ++i) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const key = parts[i]!;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access
    let nextObject = currentObject[key];
    if (nextObject === undefined || nextObject === null) {
      if (!initializeIntermediateOptionalNullableValues) return false;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      nextObject = currentObject[key] = {};
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    currentObject = nextObject;
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const key = parts[lastIndex]!;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  currentObject[key] = newValue;
  return true;
}
